import { getAccessToken } from './auth-store'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8791'

export interface ApiError {
  error: string
}

// Auth types
export interface LoginRequest {
  email: string
  password: string
}
export interface SignupRequest {
  email: string
  password: string
  name?: string
}
export interface AuthResponse {
  data: {
    access_token: string
    refresh_token: string
    user: { id: string; email: string; name: string | null }
  }
}
export interface PasswordResetRequest {
  email: string
}
export interface ResetPasswordRequest {
  token: string
  new_password: string
}
export interface VerifyEmailRequest {
  token: string
}

// Sequence types
export interface Sequence {
  id: string
  userId: string
  name: string
  description: string | null
  isActive: boolean
  isDefault: boolean
  steps: SequenceStep[]
  createdAt: string
  updatedAt: string
}

export interface SequenceStep {
  id: string
  sequenceId: string
  stepNumber: number
  delayDays: number
  subjectTemplate: string
  bodyTemplate: string
  ctaText: string | null
  ctaUrl: string | null
  discountOffer: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSequenceRequest {
  name: string
  description?: string
}
export interface UpdateSequenceRequest {
  name?: string
  description?: string
}
export interface CreateStepRequest {
  step_number: number
  delay_days: number
  subject_template: string
  body_template: string
  cta_text?: string
  cta_url?: string
  discount_offer?: string
}
export interface UpdateStepRequest {
  step_number?: number
  delay_days?: number
  subject_template?: string
  body_template?: string
  cta_text?: string
  cta_url?: string
  discount_offer?: string
}

// Cancellation types
export type CancellationStatus = 'active' | 'won_back' | 'lost' | 'paused'

export interface Cancellation {
  id: string
  customerEmail: string
  customerName: string | null
  productName: string | null
  mrr: number
  currency: string
  status: CancellationStatus
  cancelledAt: string
  wonBackAt: string | null
  surveyReason: string | null
  surveyFreeformText: string | null
  createdAt: string
}

export interface CancellationStats {
  total_cancellations: number
  total_wins: number
  win_back_rate: number
  total_mrr_lost: number
  total_mrr_recovered: number
  emails_sent: number
  open_rate: number
  click_rate: number
  currency: string
  cancellations_over_time: Array<{ date: string; count: number }>
  mrr_over_time: Array<{ date: string; amount: number }>
}

export interface SurveyResults {
  total_responses: number
  reasons: Array<{ reason: string; count: number; percentage: number }>
  freeform_responses: Array<{ text: string; cancellationId: string; createdAt: string }>
}

// A/B Test types
export interface ABTest {
  id: string
  sequenceId: string
  stepNumber: number
  variantB: Record<string, unknown>
  trafficSplit: number
  metric: string
  status: string
  winner: string | null
  createdAt: string
}

// Stripe types
export interface StripeStatus {
  connected: boolean
  stripe_account_id: string | null
  livemode: boolean
  status: string
  connected_at: string | null
  last_webhook_at: string | null
}

// Reactivation types
export interface Reactivation {
  id: string
  cancellationId: string
  reactivatedAt: string
  newMrr: number
  currency: string
}

// Settings types
export interface Settings {
  profile: { name: string | null; email: string; timezone: string | null }
  company: { company_name: string | null; product_name: string | null; checkout_url: string | null }
  billing: { plan: string; status: string }
  stripe_connection: StripeStatus
  notifications: NotificationSettings
  custom_domain: Record<string, unknown> | null
  team_count: number
  api_key_exists: boolean
}

export interface NotificationSettings {
  email_on_cancellation: boolean
  email_on_win: boolean
  email_on_connection_error: boolean
  slack_on_cancellation: boolean
  slack_on_win: boolean
  slack_daily_digest: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: { page: number; per_page: number; total: number; total_pages: number }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers }

    if (requireAuth) {
      const token = getAccessToken()
      if (token) {
        ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
      }
    }

    let response: Response
    try {
      response = await fetch(url, { ...options, headers })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('fetch') || message.includes('network') || message.includes('Failed')) {
        throw new Error(
          `Unable to connect to the API server at ${this.baseUrl}. Please check that the server is running and accessible.`
        )
      }
      throw new Error(`Network error: ${message}`)
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      if (!response.ok) {
        throw new Error(
          `Server error (${response.status}): ${response.statusText || 'Unknown error'}`
        )
      }
      throw new Error('Invalid response from server')
    }

    if (!response.ok) {
      const errorData = data as Record<string, unknown>
      const errorMessage =
        (typeof errorData.error === 'string' ? errorData.error : null) ??
        (typeof errorData.message === 'string' ? errorData.message : null) ??
        `Request failed with status ${response.status}`
      throw new Error(errorMessage)
    }

    return data as T
  }

  // Auth - NOTE: BounceBack uses snake_case for tokens
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async signup(data: SignupRequest): Promise<{ data: { id: string; email: string } }> {
    return this.request('/auth/register', { method: 'POST', body: JSON.stringify(data) })
  }

  async refreshToken(
    refreshToken: string
  ): Promise<{ data: { access_token: string; refresh_token: string } }> {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
  }

  async logout(refreshToken: string): Promise<{ data: { message: string } }> {
    return this.request(
      '/auth/logout',
      { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) },
      true
    )
  }

  async requestPasswordReset(data: PasswordResetRequest): Promise<{ data: { message: string } }> {
    return this.request('/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) })
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ data: { message: string } }> {
    return this.request('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) })
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<{ data: { message: string } }> {
    return this.request('/auth/verify-email', { method: 'POST', body: JSON.stringify(data) })
  }

  async resendVerification(data: { email: string }): Promise<{ data: { message: string } }> {
    return this.request('/auth/resend-verification', { method: 'POST', body: JSON.stringify(data) })
  }

  // Sequences
  async listSequences(): Promise<{ data: Sequence[] }> {
    return this.request('/sequences', {}, true)
  }

  async getSequence(id: string): Promise<{ data: Sequence }> {
    return this.request(`/sequences/${id}`, {}, true)
  }

  async createSequence(data: CreateSequenceRequest): Promise<{ data: Sequence }> {
    return this.request('/sequences', { method: 'POST', body: JSON.stringify(data) }, true)
  }

  async updateSequence(id: string, data: UpdateSequenceRequest): Promise<{ data: Sequence }> {
    return this.request(`/sequences/${id}`, { method: 'PUT', body: JSON.stringify(data) }, true)
  }

  async deleteSequence(id: string): Promise<{ data: { message: string } }> {
    return this.request(`/sequences/${id}`, { method: 'DELETE' }, true)
  }

  async activateSequence(id: string): Promise<{ data: Sequence }> {
    return this.request(`/sequences/${id}/activate`, { method: 'POST' }, true)
  }

  async deactivateSequence(id: string): Promise<{ data: Sequence }> {
    return this.request(`/sequences/${id}/deactivate`, { method: 'POST' }, true)
  }

  async setDefaultSequence(id: string): Promise<{ data: Sequence }> {
    return this.request(`/sequences/${id}/set-default`, { method: 'POST' }, true)
  }

  // Sequence Steps
  async createStep(sequenceId: string, data: CreateStepRequest): Promise<{ data: SequenceStep }> {
    return this.request(
      `/sequences/${sequenceId}/steps`,
      { method: 'POST', body: JSON.stringify(data) },
      true
    )
  }

  async updateStep(
    sequenceId: string,
    stepId: string,
    data: UpdateStepRequest
  ): Promise<{ data: SequenceStep }> {
    return this.request(
      `/sequences/${sequenceId}/steps/${stepId}`,
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async deleteStep(sequenceId: string, stepId: string): Promise<{ data: { message: string } }> {
    return this.request(`/sequences/${sequenceId}/steps/${stepId}`, { method: 'DELETE' }, true)
  }

  // Cancellations
  async listCancellations(params?: {
    page?: number
    per_page?: number
    status?: CancellationStatus
    search?: string
  }): Promise<PaginatedResponse<Cancellation>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.per_page !== undefined) query.set('per_page', params.per_page.toString())
    if (params?.status) query.set('status', params.status)
    if (params?.search) query.set('search', params.search)
    const qs = query.toString()
    return this.request(qs ? `/cancellations?${qs}` : '/cancellations', {}, true)
  }

  async getCancellation(id: string): Promise<{ data: Cancellation }> {
    return this.request(`/cancellations/${id}`, {}, true)
  }

  async getCancellationStats(period?: string): Promise<{ data: CancellationStats }> {
    const qs = period ? `?period=${period}` : ''
    return this.request(`/cancellations/stats${qs}`, {}, true)
  }

  async getSurveyResults(period?: string): Promise<{ data: SurveyResults }> {
    const qs = period ? `?period=${period}` : ''
    return this.request(`/cancellations/survey-results${qs}`, {}, true)
  }

  // Reactivations
  async listReactivations(params?: {
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<Reactivation>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.per_page !== undefined) query.set('per_page', params.per_page.toString())
    const qs = query.toString()
    return this.request(qs ? `/reactivations?${qs}` : '/reactivations', {}, true)
  }

  // A/B Tests
  async listABTests(): Promise<{ data: ABTest[] }> {
    return this.request('/ab-tests', {}, true)
  }

  async getABTest(id: string): Promise<{ data: ABTest }> {
    return this.request(`/ab-tests/${id}`, {}, true)
  }

  async createABTest(
    sequenceId: string,
    stepNumber: number,
    data: { variant_b: Record<string, unknown>; traffic_split?: number; metric?: string }
  ): Promise<{ data: ABTest }> {
    return this.request(
      `/sequences/${sequenceId}/steps/${stepNumber}/ab-test`,
      { method: 'POST', body: JSON.stringify(data) },
      true
    )
  }

  async declareWinner(testId: string, winner: 'a' | 'b'): Promise<{ data: ABTest }> {
    return this.request(
      `/ab-tests/${testId}/declare-winner`,
      { method: 'POST', body: JSON.stringify({ winner }) },
      true
    )
  }

  async deleteABTest(id: string): Promise<{ data: { message: string } }> {
    return this.request(`/ab-tests/${id}`, { method: 'DELETE' }, true)
  }

  // Stripe
  async getStripeConnectUrl(): Promise<{ data: { redirect_url: string } }> {
    return this.request('/stripe/connect', {}, true)
  }

  async disconnectStripe(): Promise<{ data: { message: string } }> {
    return this.request('/stripe/disconnect', { method: 'DELETE' }, true)
  }

  async getStripeStatus(): Promise<{ data: StripeStatus }> {
    return this.request('/stripe/status', {}, true)
  }

  // Settings
  async getSettings(): Promise<{ data: Settings }> {
    return this.request('/settings', {}, true)
  }

  async updateProfile(data: {
    name?: string
    email?: string
    timezone?: string
  }): Promise<{ data: { name: string; email: string; timezone: string } }> {
    return this.request('/settings/profile', { method: 'PUT', body: JSON.stringify(data) }, true)
  }

  async updateCompany(data: {
    company_name?: string
    product_name?: string
    checkout_url?: string
  }): Promise<{ data: Record<string, unknown> }> {
    return this.request('/settings/company', { method: 'PUT', body: JSON.stringify(data) }, true)
  }

  async updateNotifications(
    data: Partial<NotificationSettings>
  ): Promise<{ data: NotificationSettings }> {
    return this.request(
      '/settings/notifications',
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async deleteAccount(confirmation: string): Promise<{ data: { message: string } }> {
    return this.request(
      '/settings/account',
      { method: 'DELETE', body: JSON.stringify({ confirmation }) },
      true
    )
  }

  // Team (Business tier)
  async getTeam(): Promise<{ data: unknown[] }> {
    return this.request('/settings/team', {}, true)
  }

  async inviteTeamMember(data: { email: string; role: string }): Promise<{ data: unknown }> {
    return this.request(
      '/settings/team/invite',
      { method: 'POST', body: JSON.stringify(data) },
      true
    )
  }

  async removeTeamMember(memberId: string): Promise<{ data: { message: string } }> {
    return this.request(`/settings/team/${memberId}`, { method: 'DELETE' }, true)
  }
}

export const api = new ApiClient(API_BASE_URL)
